import React from 'react';
import ScoreTracker from './ScoreTracker';
import HistoryPanel from './HistoryPanel';

export default function SpectatorView({ spectatorState, roomCode, onLeave }) {
  const ss = spectatorState;

  if (!ss) {
    return (
      <div className="spectator-view">
        <div className="spoiler-banner">
          SPOILER VIEW - This shows all roles and game information
        </div>
        <div className="room-code-display">
          <div className="label">Spectating Room</div>
          <div className="code">{roomCode}</div>
        </div>
        <div className="waiting-state">
          <div className="waiting-icon">{'\u231B'}</div>
          Connecting to game...
        </div>
      </div>
    );
  }

  const leader = ss.players?.find(p => p.id === ss.leaderId);
  const teamNames = (ss.proposedTeam || []).map(
    tid => ss.players?.find(p => p.id === tid)?.name || '?'
  );

  const phaseLabels = {
    lobby: 'Lobby - Waiting for players',
    role_reveal: 'Role Reveal',
    team_building: `Team Building - Quest ${(ss.currentQuest || 0) + 1}`,
    voting: 'Voting on Team',
    vote_result: 'Vote Results',
    quest: `Quest ${(ss.currentQuest || 0) + 1} in Progress`,
    quest_result: `Quest ${(ss.currentQuest || 0) + 1} Result`,
    assassination: 'Assassination Phase',
    game_over: 'Game Over',
  };

  return (
    <div className="spectator-view">
      <div className="spoiler-banner">
        SPOILER VIEW - This shows all roles and game information
      </div>

      {ss.isDelayed && (
        <div className="delay-banner">
          This feed is delayed
        </div>
      )}

      <ScoreTracker gameState={ss} />

      {/* Current Phase */}
      <div className="phase-banner">
        <div className="phase-label">{phaseLabels[ss.phase] || ss.phase}</div>
        {ss.phase === 'team_building' && leader && (
          <div className="phase-detail">
            {leader.name} is selecting a team of {ss.teamSize}
          </div>
        )}
        {ss.phase === 'voting' && (
          <div className="phase-detail">
            {leader?.name}'s team: {teamNames.join(', ')} | Votes: {ss.votesSubmitted}/{ss.playerCount}
          </div>
        )}
        {ss.phase === 'quest' && (
          <div className="phase-detail">
            Team: {teamNames.join(', ')} | Cards: {ss.questCardsSubmitted}/{ss.proposedTeam?.length}
          </div>
        )}
      </div>

      {/* Game Over */}
      {ss.phase === 'game_over' && (
        <div className="game-over">
          <div className={`winner-text ${ss.winner}`}>
            {ss.winner === 'good' ? 'Good Wins!' : 'Evil Wins!'}
          </div>
          <div className="win-reason">{ss.winReason}</div>
        </div>
      )}

      {/* Players & Roles */}
      {ss.allRoles && (
        <div className="mod-section">
          <div className="mod-section-title">Players & Roles</div>
          {ss.allRoles.map(p => (
            <div key={p.id} className="mod-player-row">
              <div>
                <span className={`connected-dot ${p.connected ? '' : 'disconnected'}`} />
                {p.name}
                {p.id === ss.leaderId && <span className="leader-badge"> - Leader</span>}
                {ss.proposedTeam?.includes(p.id) && <span className="on-team"> - On Team</span>}
              </div>
              {p.role && (
                <span className={`role-badge ${
                  ['Merlin', 'Percival', 'Loyal Servant of Arthur'].includes(p.role)
                    ? 'good-role' : 'evil-role'
                }`}>
                  {p.role}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Live vote tracking */}
      {ss.phase === 'voting' && ss.allVotes && Object.keys(ss.allVotes).length > 0 && (
        <div className="mod-section">
          <div className="mod-section-title">Votes (Live)</div>
          {Object.entries(ss.allVotes).map(([pid, { name, vote }]) => (
            <div key={pid} className="vote-result-item">
              <span>{name}</span>
              <span className={`vote-badge ${vote}`}>
                {vote === 'approve' ? 'Approve' : 'Reject'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Live quest card tracking */}
      {ss.phase === 'quest' && ss.allQuestCards && Object.keys(ss.allQuestCards).length > 0 && (
        <div className="mod-section">
          <div className="mod-section-title">Quest Cards (Live)</div>
          {Object.entries(ss.allQuestCards).map(([pid, { name, card }]) => (
            <div key={pid} className="vote-result-item">
              <span>{name}</span>
              <span className={`vote-badge ${card === 'success' ? 'approve' : 'reject'}`}>
                {card === 'success' ? 'Success' : 'Fail'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Rejection tracker */}
      {ss.consecutiveRejections > 0 && ss.phase !== 'game_over' && (
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Consecutive Rejections: {ss.consecutiveRejections}/5
          </div>
        </div>
      )}

      {/* Game History */}
      <HistoryPanel gameState={ss} />

      {onLeave && (
        <button
          className="btn btn-secondary btn-full mt-16"
          onClick={onLeave}
        >
          Stop Watching
        </button>
      )}
    </div>
  );
}
